import { create } from 'zustand';
import { authService } from '../services/apiService';
import { applyTheme, normalizeTheme, readSavedTheme } from './theme';

export const useAuthStore = create((set) => ({
  user: null,
  isAuthenticated: false,
  isLoading: true,   // true until initAuth resolves — prevents flash to /login on refresh
  error: null,

  // Initialize auth from localStorage and restore user profile
  initAuth: async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      set({ isAuthenticated: false, isLoading: false });
      return;
    }
    try {
      const user = await authService.getProfile();
      set({ user, isAuthenticated: true, isLoading: false });
    } catch {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      set({ user: null, isAuthenticated: false, isLoading: false });
    }
  },

  // Login
  login: async (email, password) => {
    set({ isLoading: true, error: null });
    try {
      const response = await authService.login(email, password);
      set({
        user: response.data.user,
        isAuthenticated: true,
        isLoading: false,
      });
      return response;
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Login failed';
      set({ error: errorMsg, isLoading: false });
      throw error;
    }
  },

  // Register
  register: async (data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await authService.register(data);
      set({
        user: response.data.user,
        isAuthenticated: true,
        isLoading: false,
      });
      return response;
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Registration failed';
      set({ error: errorMsg, isLoading: false });
      throw error;
    }
  },

  // Get profile
  getProfile: async () => {
    set({ isLoading: true, error: null });
    try {
      const user = await authService.getProfile();
      set({ user, isLoading: false });
      return user;
    } catch (error) {
      set({ error: 'Failed to fetch profile', isLoading: false });
      throw error;
    }
  },

  // Update profile
  updateProfile: async (data) => {
    set({ isLoading: true, error: null });
    try {
      const user = await authService.updateProfile(data);
      set({ user, isLoading: false });
      return user;
    } catch (error) {
      set({ error: 'Failed to update profile', isLoading: false });
      throw error;
    }
  },

  // Logout
  logout: () => {
    authService.logout();
    set({ user: null, isAuthenticated: false });
  },
}));

// Light / dark mode (utils/theme.js). Signed in, the account's choice wins:
// it's applied whenever a profile arrives (sign-in, page load) and every
// change is saved to the account so it follows the user to other browsers.
export const useThemeStore = create((set, get) => ({
  theme: readSavedTheme(),
  saving: Promise.resolve(),

  // Show a theme without saving it (e.g. the one stored on the account).
  adopt: (theme) => set({ theme: applyTheme(theme) }),

  setTheme: (theme) => {
    const next = applyTheme(theme);
    set({ theme: next });
    const { isAuthenticated, user } = useAuthStore.getState();
    if (!isAuthenticated) return Promise.resolve(next);
    if (user) useAuthStore.setState({ user: { ...user, theme: next } });
    // One save at a time, in order, so quick toggling ends on the last choice.
    const saving = get().saving
      .catch(() => {})
      .then(() => authService.setTheme(next));
    set({ saving });
    return saving.then(() => next);
  },

  toggleTheme: () => get().setTheme(get().theme === 'dark' ? 'light' : 'dark'),
}));

// Whenever a user profile arrives with a theme, show it.
useAuthStore.subscribe((state, prev) => {
  const theme = state.user?.theme;
  if (theme && theme !== prev.user?.theme && normalizeTheme(theme) !== useThemeStore.getState().theme) {
    useThemeStore.getState().adopt(theme);
  }
});

export const useContentStore = create((set) => ({
  generatedContent: null,
  contentHistory: [],
  isLoading: false,
  error: null,

  setGeneratedContent: (content) => set({ generatedContent: content }),
  setContentHistory: (history) => set({ contentHistory: history }),
  setLoading: (isLoading) => set({ isLoading }),
  setError: (error) => set({ error }),
}));

export const useUIStore = create((set) => ({
  sidebarOpen: true,
  toastMessage: null,

  toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
  showToast: (message) => set({ toastMessage: message }),
  hideToast: () => set({ toastMessage: null }),
}));

// Global input text — shared across content-generation pages/tabs so the
// user's pasted source text follows them as they navigate between pages.
// Output remains page-local (see GenPage / NewsAnchorFormats / etc.).
export const useInputStore = create((set) => ({
  globalInputText: '',

  setGlobalInputText: (text) => set({ globalInputText: text }),
  clearGlobalInputText: () => set({ globalInputText: '' }),
}));
